import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  ArrowUpRight, Apple, CheckCircle2, Users, Wifi, Dice6, Sparkles, Languages, Trophy, KeySquare, Bot, Hand,
  Shield, Mail,
} from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { Section, Eyebrow, FadeIn } from '../components/ui-bits';
import Breadcrumb from '../components/Breadcrumb';

const LOGO = 'https://customer-assets.emergentagent.com/job_bringing-light/artifacts/g6u180t8_logo%20cacho.png';

const T = {
  es: {
    eyebrow: 'App · CachoDice',
    heroTitle: 'El Cacho de siempre,',
    heroTitleAccent: 'ahora en tu bolsillo.',
    heroSubtitle: 'CachoDice es el clásico juego de dados de faroleo (Cacho / Dudo / Perudo) para iPhone y iPad. Juega con amigos sin internet, con dos modos, bilingüe y prueba gratis de 7 días.',
    features: '¿Por qué CachoDice?',
    featuresTitle: 'Todo lo que necesitas para jugar Cacho, en el bolsillo.',
    featuresIntro: 'Pensado para la mesa de siempre: reúnete, conecta y juega. Rápido, íntimo y fiel a las reglas tradicionales.',
    modes: 'Dos formas de jugar',
    modesTitle: 'Elige tu estilo de mesa.',
    controlled: 'Modo Controlado',
    controlledTag: 'Automático',
    controlledDesc: 'Cada jugador ve solo sus propios dados. La app hace las predicciones y cuenta automáticamente al Dudar o Calzar.',
    controlledItems: ['Dados privados por jugador', 'Conteo automático al resolver', 'Ideal para jugar 100% digital'],
    free: 'Modo Libre',
    freeTag: 'Manual',
    freeDesc: 'Dados virtuales visibles para todos. Las predicciones y desafíos son verbales, con resolución por votación.',
    freeItems: ['Dados visibles en pantalla', 'Predicciones y desafíos verbales', 'Como jugar en la vida real'],
    how: 'Cómo funciona',
    howTitle: 'Empezar toma menos de un minuto.',
    pricing: 'Precio',
    pricingTitle: 'Prueba gratis, decide después.',
    pricingSub: '7 días con todo desbloqueado. Luego, un único pago para siempre — sin suscripciones.',
    trialTitle: 'Prueba', trialSub: 'Gratis / 7 días',
    trialItems: ['Acceso completo por 7 días', 'Modo Controlado y Libre', 'Partidas de 3 y 5 dados', 'Sin anuncios'],
    premiumTitle: 'Versión completa', premiumSub: '$2.99 / pago único',
    premiumItems: ['Todo desbloqueado para siempre', 'Modo Controlado (premium)', 'Partidas de 5 dados', 'Compra gestionada por Apple'],
    ctaClose: 'Reúne a la mesa. Reparte los dados.',
    ctaCloseSub: 'CachoDice es el compañero perfecto para tus noches de juego, viajes y reuniones. Descárgalo hoy y prueba 7 días gratis.',
    trust1: 'Sin internet', trust2: 'Sin cuentas', trust3: 'Sin publicidad',
    legalTitle: 'Todo transparente.',
    legalDesc: 'CachoDice es operada por Inversiones Tair SpA (Chile). Sin servidores, sin publicidad, sin tracking.',
    privacyLink: 'Política de Privacidad', supportLink: 'Soporte',
  },
  en: {
    eyebrow: 'App · CachoDice',
    heroTitle: 'The classic Cacho,',
    heroTitleAccent: 'now in your pocket.',
    heroSubtitle: 'CachoDice is the classic Chilean bluffing dice game (Cacho / Dudo / Perudo) for iPhone and iPad. Play with friends without internet, in two modes, bilingual, with a 7-day free trial.',
    features: 'Why CachoDice',
    featuresTitle: 'Everything you need to play Cacho, in your pocket.',
    featuresIntro: 'Built for the classic table: gather, connect and play. Fast, intimate and faithful to the traditional rules.',
    modes: 'Two ways to play',
    modesTitle: 'Pick your table style.',
    controlled: 'Controlled Mode',
    controlledTag: 'Automatic',
    controlledDesc: 'Each player only sees their own dice. The app tracks bids and settles Dudo/Calzo automatically.',
    controlledItems: ['Private dice per player', 'Automatic count on resolve', 'Perfect for 100% digital play'],
    free: 'Free Mode',
    freeTag: 'Manual',
    freeDesc: 'Virtual dice visible to everyone. Bids and challenges are verbal, resolved by voting.',
    freeItems: ['Dice visible on screen', 'Verbal bids and challenges', 'Just like playing in real life'],
    how: 'How it works',
    howTitle: 'Getting started takes less than a minute.',
    pricing: 'Pricing',
    pricingTitle: 'Try free, decide later.',
    pricingSub: '7 days fully unlocked. Then a single one-time payment forever — no subscriptions.',
    trialTitle: 'Trial', trialSub: 'Free / 7 days',
    trialItems: ['Full access for 7 days', 'Controlled and Free modes', '3 and 5 dice games', 'No ads'],
    premiumTitle: 'Full version', premiumSub: '$2.99 / one-time',
    premiumItems: ['Everything unlocked forever', 'Controlled Mode (premium)', '5 dice games', 'Purchase handled by Apple'],
    ctaClose: 'Bring the table together. Roll the dice.',
    ctaCloseSub: 'CachoDice is the perfect companion for game nights, trips and gatherings. Download today and try it free for 7 days.',
    trust1: 'No internet', trust2: 'No accounts', trust3: 'No ads',
    legalTitle: 'Everything transparent.',
    legalDesc: 'CachoDice is operated by Inversiones Tair SpA (Chile). No servers, no ads, no tracking.',
    privacyLink: 'Privacy Policy', supportLink: 'Support',
  },
};

const CachoDice = () => {
  const { lang } = useLanguage();
  const s = T[lang] || T.es;

  const features = [
    { icon: Wifi, t: lang === 'en' ? 'Offline multiplayer' : 'Multijugador sin internet', d: lang === 'en' ? 'Direct connection between nearby devices (Wi-Fi/Bluetooth) via Apple Multipeer. No servers, no data plan needed.' : 'Conexión directa entre dispositivos cercanos (Wi-Fi/Bluetooth) vía Multipeer. No necesitas servidores ni datos.' },
    { icon: Dice6, t: lang === 'en' ? 'Two game modes' : 'Dos modos de juego', d: lang === 'en' ? 'Controlled Mode (the system counts for you) and Free Mode (visible dice, manual resolution just like real life).' : 'Modo Controlado (el sistema cuenta por ti) y Modo Libre (dados visibles, resolución manual como en la vida real).' },
    { icon: Sparkles, t: lang === 'en' ? 'Wildcards & real rules' : 'Comodines & reglas reales', d: lang === 'en' ? '1s as wildcard, Dudo and Calzo, short 3-dice matches… every classic rule included.' : 'Los 1 como comodín, Dudo y Calzo, partida corta de 3 dados… todas las reglas clásicas incluidas.' },
    { icon: Languages, t: lang === 'en' ? 'Bilingual ES / EN' : 'Bilingüe ES / EN', d: lang === 'en' ? 'Switch between Spanish and English anytime. Perfect for playing with friends from anywhere.' : 'Cambia entre español e inglés en cualquier momento. Perfecto para jugar con amigos de todas partes.' },
    { icon: KeySquare, t: lang === 'en' ? 'Invite your friends' : 'Invita a tus amigos', d: lang === 'en' ? 'Share the invitation with a tap and create the room in seconds with a simple code.' : 'Comparte la invitación con un toque y crea la sala en segundos con un código simple.' },
    { icon: Trophy, t: lang === 'en' ? 'Match ranking' : 'Ranking de partidas', d: lang === 'en' ? 'Track winners and keep score of who rules the table night after night.' : 'Registra a los ganadores y lleva la cuenta de quién manda en la mesa noche tras noche.' },
  ];

  const steps = lang === 'en' ? [
    { n: '01', t: 'Download the app', d: 'Install CachoDice from the App Store on your iPhone or iPad.' },
    { n: '02', t: 'Create or join a room', d: 'The host creates the game; the rest join with a code. All local, no accounts.' },
    { n: '03', t: 'Time to bluff!', d: 'Roll, bid, Dudo and Calzo. The last player with dice on the table wins.' },
  ] : [
    { n: '01', t: 'Descarga la app', d: 'Instala CachoDice desde el App Store en tu iPhone o iPad.' },
    { n: '02', t: 'Crea o únete a una sala', d: 'El anfitrión crea la partida; los demás entran con el código. Todo local, sin cuentas.' },
    { n: '03', t: '¡A farolear!', d: 'Tira, predice, duda y calza. Gana el último con dados en la mesa.' },
  ];

  return (
    <div data-testid="cachodice-marketing">
      <Breadcrumb items={[
        { to: '/que-hacemos', label: lang === 'en' ? 'What We Do' : 'Qué Hacemos' },
        { to: '/que-hacemos/desarrollo-software', label: 'Software' },
        { to: null, label: 'CachoDice' },
      ]} />

      <section className="relative overflow-hidden">
        <div className="absolute inset-0 light-beam" />
        <div className="relative z-10 max-w-7xl mx-auto w-full px-6 md:px-10 lg:px-12 pt-4 pb-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-7">
              <FadeIn><Eyebrow>{s.eyebrow}</Eyebrow></FadeIn>
              <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1], delay: 0.1 }} className="mt-8 font-display font-extrabold tracking-tight text-white text-4xl sm:text-5xl lg:text-6xl leading-[1.05]">
                {s.heroTitle}<br /><span className="text-amber-300">{s.heroTitleAccent}</span>
              </motion.h1>
              <FadeIn delay={0.25}><p className="mt-8 max-w-xl font-body text-base sm:text-lg text-white/65 leading-relaxed">{s.heroSubtitle}</p></FadeIn>
              <FadeIn delay={0.35}>
                <div className="mt-10 flex flex-wrap gap-3">
                  <a href="https://apps.apple.com/us/app/cachodice-cacho-dudo/id6760022330" target="_blank" rel="noopener noreferrer" data-testid="cacho-appstore" className="inline-flex items-center gap-3 h-13 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors">
                    <Apple className="w-5 h-5" />
                    <div className="flex flex-col items-start leading-tight">
                      <span className="text-[10px] uppercase tracking-widest text-black/60">{lang === 'en' ? 'Download on the' : 'Descargar en'}</span>
                      <span>App Store</span>
                    </div>
                  </a>
                  <a href="mailto:info@tair.site" className="inline-flex items-center gap-2 h-12 px-6 rounded-full bg-transparent border border-white/20 text-white/85 font-medium text-sm hover:bg-white/5 hover:border-white/40 transition-colors">{lang === 'en' ? 'Write to info@tair.site' : 'Escribir a info@tair.site'}</a>
                </div>
              </FadeIn>
              <FadeIn delay={0.5}>
                <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-xs text-white/50">
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust1}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust2}</span>
                  <span className="inline-flex items-center gap-1.5"><CheckCircle2 className="w-3.5 h-3.5 text-amber-300/70" /> {s.trust3}</span>
                </div>
              </FadeIn>
            </div>
            <div className="lg:col-span-5 flex justify-center lg:justify-end">
              <FadeIn delay={0.35}>
                <div className="relative">
                  <div className="absolute inset-0 blur-3xl bg-emerald-400/25 rounded-full" />
                  <div className="relative w-56 h-56 md:w-72 md:h-72 rounded-[3rem] overflow-hidden shadow-2xl border border-white/10 bg-[#0a1322]">
                    <img src={LOGO} alt="CachoDice" className="w-full h-full object-cover" />
                  </div>
                  <div className="absolute -bottom-4 -right-4 text-[10px] uppercase tracking-widest text-white/40 bg-[#070d18]/80 border border-white/10 rounded-full px-3 py-1.5 backdrop-blur">iOS · iPhone · iPad</div>
                </div>
              </FadeIn>
            </div>
          </div>
        </div>
      </section>

      <Section className="py-24" dataTestId="cacho-features">
        <FadeIn><Eyebrow>{s.features}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}><h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.featuresTitle}</h2></FadeIn>
        <FadeIn delay={0.15}><p className="mt-6 max-w-2xl font-body text-white/60">{s.featuresIntro}</p></FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {features.map((f, i) => { const Icon = f.icon; return (
            <FadeIn key={i} delay={i * 0.06}>
              <div className="rounded-2xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="w-11 h-11 rounded-xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300"><Icon className="w-5 h-5" /></div>
                <h4 className="mt-6 font-display font-bold text-lg md:text-xl text-white tracking-tight">{f.t}</h4>
                <p className="mt-3 font-body text-sm text-white/55 leading-relaxed">{f.d}</p>
              </div>
            </FadeIn>
          ); })}
        </div>
      </Section>

      <Section className="pb-24" dataTestId="cacho-modes">
        <FadeIn><Eyebrow>{s.modes}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}><h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.modesTitle}</h2></FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-5">
          {[
            { icon: Bot, tag: s.controlledTag, title: s.controlled, desc: s.controlledDesc, items: s.controlledItems, tone: 'from-amber-300/[0.08] to-transparent' },
            { icon: Hand, tag: s.freeTag, title: s.free, desc: s.freeDesc, items: s.freeItems, tone: 'from-emerald-300/[0.08] to-transparent' },
          ].map((m, i) => { const Icon = m.icon; return (
            <FadeIn key={i} delay={i * 0.1}>
              <div className={`rounded-3xl border border-white/10 bg-gradient-to-br ${m.tone} p-10 h-full card-hover relative overflow-hidden`}>
                <div className="flex items-start justify-between mb-6">
                  <div className="w-12 h-12 rounded-2xl bg-amber-300/10 border border-amber-300/20 flex items-center justify-center text-amber-300"><Icon className="w-5 h-5" /></div>
                  <span className="text-[10px] uppercase tracking-[0.2em] text-white/45">{m.tag}</span>
                </div>
                <h3 className="font-display font-bold text-2xl md:text-3xl text-white tracking-tight">{m.title}</h3>
                <p className="mt-4 font-body text-white/65 leading-relaxed">{m.desc}</p>
                <ul className="mt-6 space-y-2.5">
                  {m.items.map((it, j) => (<li key={j} className="flex items-start gap-2.5 text-white/80"><CheckCircle2 className="w-4 h-4 text-amber-300 mt-1 flex-shrink-0" /><span>{it}</span></li>))}
                </ul>
              </div>
            </FadeIn>
          ); })}
        </div>
      </Section>

      <Section className="pb-24" dataTestId="cacho-how">
        <FadeIn><Eyebrow>{s.how}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}><h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.howTitle}</h2></FadeIn>
        <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-5">
          {steps.map((step, i) => (
            <FadeIn key={i} delay={i * 0.08}>
              <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-8 h-full card-hover">
                <div className="font-display text-amber-300 text-sm tracking-widest">{step.n}</div>
                <h4 className="mt-4 font-display font-bold text-xl text-white tracking-tight">{step.t}</h4>
                <p className="mt-3 font-body text-white/60 leading-relaxed">{step.d}</p>
              </div>
            </FadeIn>
          ))}
        </div>
      </Section>

      <Section className="pb-24" dataTestId="cacho-pricing">
        <FadeIn><Eyebrow>{s.pricing}</Eyebrow></FadeIn>
        <FadeIn delay={0.08}><h2 className="mt-6 font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl leading-[1.1]">{s.pricingTitle}</h2></FadeIn>
        <FadeIn delay={0.15}><p className="mt-6 max-w-2xl font-body text-white/60">{s.pricingSub}</p></FadeIn>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-5">
          <FadeIn>
            <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 h-full card-hover">
              <div className="text-xs uppercase tracking-[0.2em] text-white/45">{s.trialSub}</div>
              <h3 className="mt-3 font-display font-extrabold text-4xl md:text-5xl text-white tracking-tight">{s.trialTitle}</h3>
              <ul className="mt-8 space-y-3">
                {s.trialItems.map((it, i) => (<li key={i} className="flex items-start gap-2.5 text-white/70"><CheckCircle2 className="w-4 h-4 text-white/50 mt-1 flex-shrink-0" /><span>{it}</span></li>))}
              </ul>
            </div>
          </FadeIn>
          <FadeIn delay={0.1}>
            <div className="rounded-3xl border border-amber-300/40 bg-gradient-to-br from-amber-300/[0.08] to-transparent p-10 h-full card-hover relative overflow-hidden">
              <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-amber-300/10 blur-3xl pointer-events-none" />
              <div className="relative">
                <div className="text-xs uppercase tracking-[0.2em] text-amber-300">{s.premiumSub}</div>
                <h3 className="mt-3 font-display font-extrabold text-4xl md:text-5xl text-white tracking-tight">{s.premiumTitle}</h3>
                <ul className="mt-8 space-y-3">
                  {s.premiumItems.map((it, i) => (<li key={i} className="flex items-start gap-2.5 text-white/85"><CheckCircle2 className="w-4 h-4 text-amber-300 mt-1 flex-shrink-0" /><span>{it}</span></li>))}
                </ul>
              </div>
            </div>
          </FadeIn>
        </div>
      </Section>

      <Section className="pb-24" dataTestId="cacho-cta-close">
        <FadeIn>
          <div className="rounded-3xl border border-white/10 bg-gradient-to-br from-emerald-500/[0.06] to-transparent p-10 md:p-14 text-center">
            <h2 className="font-display font-extrabold tracking-tight text-3xl md:text-4xl lg:text-5xl text-white max-w-3xl mx-auto leading-[1.1]">{s.ctaClose}</h2>
            <p className="mt-6 max-w-2xl mx-auto font-body text-white/65">{s.ctaCloseSub}</p>
            <a href="https://apps.apple.com/us/app/cachodice-cacho-dudo/id6760022330" target="_blank" rel="noopener noreferrer" className="mt-8 inline-flex items-center gap-3 h-13 px-6 py-3 rounded-full bg-white text-[#0a1322] font-semibold text-sm hover:bg-amber-100 transition-colors">
              <Apple className="w-5 h-5" />
              <div className="flex flex-col items-start leading-tight">
                <span className="text-[10px] uppercase tracking-widest text-black/60">{lang === 'en' ? 'Download on the' : 'Descargar en'}</span>
                <span>App Store</span>
              </div>
            </a>
          </div>
        </FadeIn>
      </Section>

      <Section className="pb-32" dataTestId="cacho-legal-section">
        <FadeIn>
          <div className="rounded-3xl border border-white/10 bg-white/[0.025] p-10 md:p-14">
            <Eyebrow>{lang === 'en' ? 'Legal & Support' : 'Legal y Soporte'}</Eyebrow>
            <h3 className="mt-5 font-display font-extrabold text-2xl md:text-3xl text-white tracking-tight">{s.legalTitle}</h3>
            <p className="mt-4 max-w-2xl font-body text-white/60">{s.legalDesc}</p>
            <div className="mt-8 flex flex-wrap gap-3">
              <Link to="/cachodice/privacy" data-testid="cacho-privacy-link" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors">
                <Shield className="w-4 h-4" /> {s.privacyLink} <ArrowUpRight className="w-3.5 h-3.5" />
              </Link>
              <a href="mailto:info@tair.site" data-testid="cacho-support-link" className="inline-flex items-center gap-2 text-sm text-white/80 hover:text-amber-300 px-4 py-2 rounded-full border border-white/15 hover:border-amber-300/40 transition-colors">
                <Mail className="w-4 h-4" /> {s.supportLink} · info@tair.site <ArrowUpRight className="w-3.5 h-3.5" />
              </a>
            </div>
          </div>
        </FadeIn>
      </Section>
    </div>
  );
};

export default CachoDice;
